import { useState } from 'react'
import { MoreHorizontal } from 'lucide-react'

import { cn } from '@/utils'
import { Popover, PopoverTrigger } from '@/components/ui/popover'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'

import { TRIGGER_CLASS } from '../constants/format-control-classes'
import { resolveSpacingFieldConfig } from '../helpers/spacing-field-config'
import type { ControlProps } from '../types/control-props'
import type { SpacingMode } from '../types/spacing-mode'
import { FormatPopoverContent } from './format-popover-content'
import { NumericPresetField } from './numeric-preset-field'

/**
 * One tab of the Kerning|Tracking strip, at the design's metrics (node 20446:483097): the
 * SELECTED tab is white and shadowed inside a `--muted` container.
 *
 * `toggleVariants` ships the inverse — `data-[state=on]:bg-accent`, and `--accent` and `--muted`
 * are the same #f5f5f5, so the selection was painting the container's own colour. Both the base
 * and the hover `data-[state=on]:` keys need overriding (tailwind-merge treats them as separate
 * groups), and the OFF state needs its own hover too: the variant's `hover:bg-muted` is muted on
 * muted, i.e. no feedback at all.
 */
const TAB_TRIGGER_CLASS =
  'h-full rounded-md text-sm font-medium data-[state=on]:bg-background data-[state=on]:text-foreground data-[state=on]:shadow-sm data-[state=on]:hover:bg-background data-[state=off]:hover:bg-background/60 data-[state=off]:hover:text-foreground'

/**
 * AC-6's "Slot": one segmented `Kerning | Tracking` toggle over a single shared body of presets,
 * slider and `em` input.
 *
 * The toggle is local UI state, NOT a field on the config. Both values stay live and both apply —
 * `resolveTextOnPathTypography` sums them into one letter-spacing channel, which is the convention
 * everywhere text is drawn in this codebase. The tab only chooses which of the two the shared body
 * is bound to.
 *
 * Unlike the Type panel, neither tab is ever disabled here. There, Kerning is scoped to the glyph
 * pair at the caret and so needs a gate; text on a path is a plain string with no caret and no run
 * model, so both values apply to the whole text.
 */
export function TextOnPathSpacingControl({
  config,
  onConfigChange,
  onRestoreFocus,
  className,
}: ControlProps) {
  // Tracking first: it is the whole-text control users reach for, and the one this model makes the
  // most meaningful.
  const [mode, setMode] = useState<SpacingMode>('tracking')
  const field = resolveSpacingFieldConfig(mode, config)

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Tracking and kerning"
          className={cn(TRIGGER_CLASS, className)}
        >
          <MoreHorizontal className="h-4 w-4" />
        </button>
      </PopoverTrigger>
      <FormatPopoverContent className="w-[320px]" onRestoreFocus={onRestoreFocus}>
        <div className="flex flex-col gap-4">
          {/*
            No `Slot` heading — there is none in the design. The one that used to sit here was the
            Figma *slot* node's own name, picked up verbatim during the design-to-code pass. The
            tab strip itself is `TAB_TRIGGER_CLASS` above.
          */}
          <ToggleGroup
            type="single"
            variant="outline"
            size="sm"
            value={mode}
            onValueChange={(next) => {
              if (next) setMode(next as SpacingMode)
            }}
            className="grid h-9 w-full grid-cols-2 gap-0 rounded-lg bg-muted p-[3px]"
          >
            <ToggleGroupItem value="kerning" className={TAB_TRIGGER_CLASS}>
              Kerning
            </ToggleGroupItem>
            <ToggleGroupItem value="tracking" className={TAB_TRIGGER_CLASS}>
              Tracking
            </ToggleGroupItem>
          </ToggleGroup>
          {/*
            Keyed by mode: switching tabs remounts the field, so its raw input string and any
            pending error belong to the value now bound rather than the one just left behind.
          */}
          <NumericPresetField
            key={mode}
            label={field.label}
            hideLabel
            value={field.value}
            min={field.min}
            max={field.max}
            step={1}
            presets={field.presets}
            presetLabel={field.presetLabel}
            suffix="em"
            onCommit={(next) => onConfigChange(field.toPatch(next))}
          />
        </div>
      </FormatPopoverContent>
    </Popover>
  )
}
